import type { SVGProps } from 'react';
import { palette } from '../tokens.ts';

export interface CompassRoseProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  size?: number | string;
  ink?: string;
  paper?: string;
  accent?: string;
  /** Letter drawn above the north point; pass an empty string to omit. */
  northLabel?: string;
  title?: string;
}

const polar = (angleDeg: number, radius: number): [number, number] => {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [Math.cos(a) * radius, Math.sin(a) * radius];
};
const pt = ([x, y]: [number, number]) => `${x.toFixed(2)},${y.toFixed(2)}`;

/** One point of the rose, split into an inked half and a paper half, as on engraved charts. */
function RosePoint({
  angle,
  length,
  width,
  ink,
  paper,
}: {
  angle: number;
  length: number;
  width: number;
  ink: string;
  paper: string;
}) {
  const tip = polar(angle, length);
  const left = polar(angle - 45, width);
  const right = polar(angle + 45, width);
  return (
    <g strokeLinejoin="round">
      <polygon points={`0,0 ${pt(left)} ${pt(tip)}`} fill={ink} stroke={ink} strokeWidth={0.6} />
      <polygon points={`0,0 ${pt(right)} ${pt(tip)}`} fill={paper} stroke={ink} strokeWidth={0.6} />
    </g>
  );
}

/**
 * A 32-point compass rose in the manner of mid-19th-century Admiralty charts.
 * Decorative by default (aria-hidden) unless a `title` is supplied.
 */
export function CompassRose({
  size = 200,
  ink = palette.ink,
  paper = palette.paper,
  accent = palette.sealRed,
  northLabel = 'N',
  title,
  ...rest
}: CompassRoseProps) {
  const ticks = Array.from({ length: 128 }, (_, i) => i * (360 / 128));
  const cardinal = [0, 90, 180, 270];
  const intercardinal = [45, 135, 225, 315];
  const minor = Array.from({ length: 8 }, (_, i) => 22.5 + i * 45);
  const fine = Array.from({ length: 16 }, (_, i) => 11.25 + i * 22.5);

  return (
    <svg
      viewBox="-110 -118 220 228"
      width={size}
      height={size}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {/* Graduated rings */}
      <circle r={100} fill="none" stroke={ink} strokeWidth={1.4} />
      <circle r={96} fill="none" stroke={ink} strokeWidth={0.5} />
      <circle r={86} fill="none" stroke={ink} strokeWidth={0.8} />
      {ticks.map((angle, i) => {
        const long = i % 4 === 0;
        const [x1, y1] = polar(angle, 96);
        const [x2, y2] = polar(angle, long ? 86 : 91);
        return (
          <line key={angle} x1={x1} y1={y1} x2={x2} y2={y2} stroke={ink} strokeWidth={long ? 0.7 : 0.35} />
        );
      })}
      <circle r={44} fill="none" stroke={ink} strokeWidth={0.5} strokeDasharray="1.5 2.5" />

      {/* Points, back to front */}
      {fine.map((angle) => {
        const [x, y] = polar(angle, 58);
        return <line key={angle} x1={0} y1={0} x2={x} y2={y} stroke={ink} strokeWidth={0.5} />;
      })}
      {minor.map((angle) => (
        <RosePoint key={angle} angle={angle} length={64} width={8} ink={ink} paper={paper} />
      ))}
      {intercardinal.map((angle) => (
        <RosePoint key={angle} angle={angle} length={74} width={12} ink={ink} paper={paper} />
      ))}
      {cardinal.map((angle) => (
        <RosePoint key={angle} angle={angle} length={98} width={16} ink={ink} paper={paper} />
      ))}

      {/* Hub */}
      <circle r={7} fill={paper} stroke={ink} strokeWidth={0.8} />
      <circle r={3} fill={accent} stroke={ink} strokeWidth={0.5} />

      {/* North mark: a small star above the north point */}
      {northLabel ? (
        <text x={0} y={-106} textAnchor="middle" fontFamily="'PT Serif', serif" fontSize={14} fill={accent}>
          {northLabel}
        </text>
      ) : null}
    </svg>
  );
}
